import { useState } from 'react'
import {
  Newspaper,
  Plus,
  Pencil,
  Trash2,
  Search,
  Image as ImageIcon,
  Link as LinkIcon,
} from 'lucide-react'
import { Card, CardContent } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { Breadcrumbs } from '../components/layout/Breadcrumbs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog'
import { NewsForm } from '../components/news/NewsForm'
import { useNews, useDeleteNews, type NewsItem } from '../hooks/news/useNews'

export function News() {
  const [search, setSearch] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<NewsItem | null>(null)
  const [deleting, setDeleting] = useState<NewsItem | null>(null)

  const { data, isLoading } = useNews({ search: search || undefined, limit: 100 })
  const deleteNews = useDeleteNews()

  const items = data?.items ?? []

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const openEdit = (item: NewsItem) => {
    setEditing(item)
    setFormOpen(true)
  }

  const confirmDelete = async () => {
    if (!deleting) return
    await deleteNews.mutateAsync(deleting.id)
    setDeleting(null)
  }

  return (
    <div className="space-y-4">
      <Breadcrumbs />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Newspaper className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-semibold">Новости</h1>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" /> Создать новость
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по заголовку и тексту..."
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Загрузка...</p>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-gray-500">
            Новостей пока нет
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <Card key={item.id}>
              <CardContent className="flex items-start justify-between gap-4 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-medium">{item.title}</h3>
                    {item.is_published ? (
                      <Badge variant="default">Опубликовано</Badge>
                    ) : (
                      <Badge variant="secondary">Черновик</Badge>
                    )}
                    {item.is_all_organizations ? (
                      <Badge variant="outline">Все организации</Badge>
                    ) : (
                      <Badge variant="outline">
                        Организаций: {item.organization_ids.length}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 line-clamp-2 text-sm text-gray-500">
                    {item.content}
                  </p>
                  <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                    {item.images.length > 0 && (
                      <span className="flex items-center gap-1">
                        <ImageIcon className="h-3 w-3" /> {item.images.length}
                      </span>
                    )}
                    {item.links.length > 0 && (
                      <span className="flex items-center gap-1">
                        <LinkIcon className="h-3 w-3" /> {item.links.length}
                      </span>
                    )}
                    <span>
                      {new Date(item.created_at).toLocaleDateString('ru-RU')}
                    </span>
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(item)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleting(item)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {formOpen && (
        <NewsForm
          open={formOpen}
          editing={editing}
          onClose={() => setFormOpen(false)}
        />
      )}

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div>
              <DialogTitle>Удалить новость?</DialogTitle>
              <DialogDescription>
                «{deleting?.title}» будет удалена безвозвратно.
              </DialogDescription>
            </div>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteNews.isPending}
            >
              Удалить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
